/**
 * @file Approval notification routing by approval domain.
 */

const ACTIVE_APPROVAL_STATUSES = new Set(["pending", "escalated"]);
const APPROVAL_DOMAINS = Object.freeze({
  DRUGS: "drugs",
  LEAVE: "leave",
});

/**
 * Opens a confirmation modal for approval routing outcomes.
 * @param {string} title Modal title.
 * @param {string} message Modal message.
 * @returns {Promise<void>}
 */
async function showApprovalRoutingMessage(title, message) {
  const { openConfirmationModal } =
    await import("../../shared/ui/confirmationModal.js");
  openConfirmationModal(title, message, () => {}, false, "OK");
}

/**
 * Resolves the approval domain from durable route metadata or approval data.
 * @param {object} params Domain resolution params.
 * @param {object} params.notification Notification document data.
 * @param {object} params.target Resolved notification click target.
 * @param {object} params.approvalData Approval document data.
 * @returns {string|null} Approval domain id.
 */
function resolveApprovalDomain({
  notification,
  target,
  approvalData,
}) {
  if (target?.approvalDomain) {return target.approvalDomain;}
  if (notification?.approvalDomain) {return notification.approvalDomain;}
  if (approvalData?.domain) {return approvalData.domain;}

  return null;
}

/**
 * Opens the approval UI for a supported approval domain.
 * @param {object} params Open params.
 * @param {string} params.approvalId Approval document id.
 * @param {object} params.approvalData Approval document data.
 * @param {string|null} params.approvalDomain Approval domain id.
 * @returns {Promise<boolean>} True when a domain handler opened the approval.
 */
async function openApprovalDomainModal({
  approvalId,
  approvalData,
  approvalDomain,
}) {
  if (approvalDomain === APPROVAL_DOMAINS.DRUGS) {
    const { renderApprovalModal } =
      await import("../../domains/drugs/drugApprovalModals.js");
    renderApprovalModal(approvalId, approvalData);
    return true;
  }
  if (approvalDomain === APPROVAL_DOMAINS.LEAVE) {
    const { openLeaveApprovalModal } =
      await import("../../domains/calendar/leaveDetailModal.js");
    openLeaveApprovalModal(approvalId, approvalData);
    return true;
  }

  return false;
}

/**
 * Opens an approval notification target.
 * @param {object} params Router params.
 * @param {object} params.notification Notification document data.
 * @param {object} params.target Resolved notification click target.
 * @param {Function} params.markNotificationRead Marks a notification read.
 * @returns {Promise<void>}
 */
export async function openApprovalNotificationTarget({
  notification,
  target,
  markNotificationRead,
}) {
  try {
    const { doc, getDoc } =
      await import("https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js");
    const { db } = await import("../firebase/firebase.js");

    const approvalDoc = await getDoc(doc(db, "approvals", target.approvalId));
    if (!approvalDoc.exists()) {
      console.error("Approval document not found:", target.approvalId);
      await showApprovalRoutingMessage(
        "Error",
        "Approval request not found. It may have already been processed.",
      );
      markNotificationRead(notification.id);
      return;
    }

    const approvalData = approvalDoc.data();
    if (!ACTIVE_APPROVAL_STATUSES.has(approvalData.status)) {
      markNotificationRead(notification.id);
      await showApprovalRoutingMessage(
        "Request Finalised",
        "This approval request has already been finalised.",
      );
      return;
    }

    const approvalDomain = resolveApprovalDomain({
      notification,
      target,
      approvalData,
    });
    const opened = await openApprovalDomainModal({
      approvalId: target.approvalId,
      approvalData,
      approvalDomain,
    });
    if (opened) {return;}

    console.error("Unsupported approval notification domain:", {
      approvalId: target.approvalId,
      approvalDomain,
    });
    await showApprovalRoutingMessage(
      "Unsupported Approval",
      "This approval type is not available in this version.",
    );
  } catch (error) {
    console.error("Error opening approval modal:", error);
    await showApprovalRoutingMessage(
      "Error",
      "Failed to load approval details. Please try again.",
    );
  }
}

export const approvalNotificationRouterInternals = Object.freeze({
  APPROVAL_DOMAINS,
  resolveApprovalDomain,
});
